import React from 'react';
import { WaitConfiguration } from '../../../../types/workflow';
import DateTimePickerInput from '../../../../components/DateTimePickerInput';

interface WaitConfigProps {
  configuration: WaitConfiguration;
  onChange: (config: WaitConfiguration) => void;
  isReadOnly: boolean;
}

const WaitConfig: React.FC<WaitConfigProps> = ({
  configuration,
  onChange,
  isReadOnly,
}) => {
  const updateConfig = (field: keyof WaitConfiguration, value: any) => {
    onChange({
      ...configuration,
      [field]: value,
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Wait Type
        </label>
        <select
          value={configuration.waitType || 'time'}
          onChange={(e) => updateConfig('waitType', e.target.value as 'time' | 'event' | 'condition')}
          disabled={isReadOnly}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
        >
          <option value="time">Time Duration</option>
          <option value="event">Wait for Event</option>
          <option value="condition">Wait for Condition</option>
        </select>
      </div>

      {configuration.waitType === 'time' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Duration (seconds)
            </label>
            <input
              type="number"
              value={configuration.duration || ''}
              onChange={(e) => updateConfig('duration', parseInt(e.target.value) || undefined)}
              disabled={isReadOnly}
              placeholder="Enter wait duration in seconds"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
            />
            <p className="mt-1 text-xs text-gray-500">
              Workflow will pause for the specified duration
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Until Date/Time
            </label>
            <DateTimePickerInput
              value={configuration.untilDate || ''}
              onChange={(value) => updateConfig('untilDate', value)}
              disabled={isReadOnly}
              className="mt-1"
            />
            <p className="mt-1 text-xs text-gray-500">
              Wait until a specific date and time (overrides duration)
            </p>
          </div>
        </>
      )}

      {configuration.waitType === 'event' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Event Name
            </label>
            <input
              type="text"
              value={configuration.eventName || ''}
              onChange={(e) => updateConfig('eventName', e.target.value)}
              disabled={isReadOnly}
              placeholder="Enter event name to wait for"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Event Source
            </label>
            <select
              value={configuration.eventSource || 'system'}
              onChange={(e) => updateConfig('eventSource', e.target.value)}
              disabled={isReadOnly}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
            >
              <option value="system">System</option>
              <option value="external">External API</option>
              <option value="webhook">Webhook</option>
              <option value="user">User Action</option>
            </select>
          </div>
        </>
      )}

      {configuration.waitType === 'condition' && (
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Condition Expression
          </label>
          <textarea
            value={configuration.condition || ''}
            onChange={(e) => updateConfig('condition', e.target.value)}
            disabled={isReadOnly}
            placeholder="Enter JavaScript expression to evaluate"
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm font-mono disabled:bg-gray-100"
          />
          <p className="mt-1 text-xs text-gray-500">
            Workflow will wait until this condition evaluates to true
          </p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Maximum Wait Time (seconds)
        </label>
        <input
          type="number"
          value={configuration.maxWait || ''}
          onChange={(e) => updateConfig('maxWait', parseInt(e.target.value) || undefined)}
          disabled={isReadOnly}
          placeholder="Maximum time to wait (optional)"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
        />
        <p className="mt-1 text-xs text-gray-500">
          Workflow will continue after this timeout even if condition is not met
        </p>
      </div>

      <div className="bg-blue-50 p-3 rounded-md">
        <p className="text-xs text-blue-700">
          <strong>Note:</strong> Wait activities pause workflow execution until the specified condition is met.
        </p>
        <p className="mt-1 text-xs text-blue-600">
          Use time-based waits for scheduled activities, event-based for integrations, and condition-based for dynamic logic.
        </p>
      </div>
    </div>
  );
};

export default WaitConfig;